import type { Document } from '@/lib/mockData';
import type { DetectedTaskEvent, TasksDetectedEvent } from '@/lib/streamingClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

interface DBTask {
  id: number;
  type: string;
  title: string;
  description?: string;
  completed: boolean;
  has_referral_attempt?: boolean;
  extra_data?: {
    confidence?: number;
  };
  patient?: { id?: number | null } | null;
}

/** React Query cache key for a session's tasks — shared with useNoteInstructions so both stay in sync. */
export const tasksQueryKey = (sessionId?: string | null) => ['tasks', sessionId] as const;

/** Task types whose document RequestLetterSheet generates from `request_document.source_details`. */
export const REQUEST_DOCUMENT_TYPES: readonly string[] = ['referral', 'consultant_request', 'radiology_request'];

export const DB_TYPE_TO_FRONTEND: Record<string, string> = {
  lyfseðill: 'prescription',
  atvinnurekandavottorð: 'certificate',
  endurbókun: 'booking',
  blóðprufur: 'bloodtest',
  tilvísun: 'referral',
  samantektarbréf: 'patient_letter',
  ráðgjafabeiðni: 'consultant_request',
  röntgenmyndir: 'radiology_request',
  almennt_verkefni: 'general_task',
};

const convertServerTasksToDocuments = (tasks: DetectedTaskEvent[]): Document[] =>
  tasks.map((task, index) => ({
    id: task.db_id ? `db-${task.db_id}` : `detected-${Date.now()}-${index}`,
    type: (task.type as Document['type']) || 'patient_letter',
    title: task.title,
    details: task.details,
    status: 'Drög' as const,
    confidence: task.confidence,
    dbId: task.db_id,
    extraData:
      task.extra_data ??
      (REQUEST_DOCUMENT_TYPES.includes(task.type)
        ? { source: 'ai_detection', request_document: { source_details: task.details, generated: false } }
        : null),
  }));

interface UseTasksOptions {
  sessionId?: string | null;
  enabled: boolean; // hasVerkefniAccess
}

export const useTasks = ({ sessionId, enabled }: UseTasksOptions) => {
  const queryClient = useQueryClient();

  // DB-loaded tasks. The React Query cache is the single source of truth: the DB
  // load populates it and the toggle mutation writes straight back into it via
  // setQueryData (no parallel state to drift).
  const queryKey = tasksQueryKey(sessionId);

  // Server-detected tasks (from WebSocket after smart review) — kept separate
  // because they arrive over the socket, not from the REST cache.
  const [serverTasks, setServerTasks] = useState<Document[]>([]);

  const { data: dbTasks = [] } = useQuery({
    queryKey,
    enabled: Boolean(sessionId && enabled),
    queryFn: async (): Promise<Document[]> => {
      const params = new URLSearchParams({ session_id: String(sessionId) });
      const response = await fetch(`/api/tasks?${params}`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Failed to load tasks: ${response.status}`);
      }
      const data = await response.json();
      return ((data.todo_items as DBTask[]) || []).map((task) => ({
        id: `db-${task.id}`,
        type: (DB_TYPE_TO_FRONTEND[task.type] || task.type) as Document['type'],
        title: task.title,
        details: task.description || '',
        status: task.completed ? ('Lokið' as const) : ('Drög' as const),
        confidence: task.extra_data?.confidence || 0.9,
        dbId: task.id,
        extraData: task.extra_data ?? null,
        patientDbId: task.patient?.id ?? null,
        hasReferralAttempt: task.has_referral_attempt === true,
      }));
    },
  });

  // Handle server-detected tasks (called from WebSocket handler — no useEffect)
  const handleServerTasks = useCallback(
    (event: TasksDetectedEvent) => {
      if (sessionId) {
        // Detection output may omit protected completed referrals or contain a
        // suppressed replacement. Read the persisted list for socket events too.
        setServerTasks([]);
        void queryClient.invalidateQueries({ queryKey: tasksQueryKey(sessionId) });
        return;
      }
      if (event.tasks?.length) setServerTasks(convertServerTasksToDocuments(event.tasks));
    },
    [queryClient, sessionId]
  );

  // Toggle task completion in DB.
  const toggleMutation = useMutation({
    mutationFn: async ({ doc, completed }: { doc: Document; completed: boolean }) => {
      const dbId = doc.id.startsWith('db-') ? Number.parseInt(doc.id.slice(3)) : doc.dbId;
      if (!dbId) throw new Error('Missing task id');
      const response = await fetch(`/api/tasks/${dbId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // Omit user_id — the server derives it from the authenticated session;
        // a client-supplied user_id would be an impersonation surface.
        body: JSON.stringify({ completed }),
      });
      if (!response.ok) {
        throw new Error(`Failed to toggle task: ${response.status}`);
      }
    },
    onSuccess: (_data, { doc, completed }) => {
      const newStatus = completed ? ('Lokið' as const) : ('Drög' as const);
      const updateStatus = (prev: Document[] = []) =>
        prev.map((t) => (t.id === doc.id ? { ...t, status: newStatus } : t));
      queryClient.setQueryData<Document[]>(queryKey, updateStatus);
      setServerTasks(updateStatus);
    },
    onError: (error) => {
      console.error('[useTasks] Error toggling completion:', error);
    },
  });

  const { mutateAsync: toggleMutateAsync } = toggleMutation;
  const toggleCompletion = useCallback(
    async (doc: Document, completed: boolean) => {
      const dbId = doc.id.startsWith('db-') ? Number.parseInt(doc.id.slice(3)) : doc.dbId;
      if (!dbId || doc.hasReferralAttempt) return;
      // Swallow errors at the boundary (mutation already logs via onError) so
      // callers keep the original fire-and-forget behavior.
      await toggleMutateAsync({ doc, completed }).catch(() => {});
    },
    [toggleMutateAsync]
  );

  // Merge cached (DB + detected) tasks with server (socket) tasks, dedup by dbId.
  const tasks = useMemo(() => {
    const cachedDbIds = new Set(dbTasks.filter((t) => t.dbId).map((t) => t.dbId));
    const uniqueServerTasks = serverTasks.filter((t) => !t.dbId || !cachedDbIds.has(t.dbId));
    return [...dbTasks, ...uniqueServerTasks];
  }, [dbTasks, serverTasks]);

  // Completed tasks, derived from the merged list (no separate state to drift).
  const confirmedTasks = useMemo(() => new Set(tasks.filter((t) => t.status === 'Lokið').map((t) => t.id)), [tasks]);

  // Reconcile with the DB: clear stale socket tasks and refetch the
  // authoritative persisted list.
  const refreshTasks = useCallback(() => {
    setServerTasks([]);
    queryClient.invalidateQueries({ queryKey: tasksQueryKey(sessionId) });
  }, [queryClient, sessionId]);

  const updateTaskContent = useCallback(
    (doc: Document, content: string, extraData: Record<string, unknown>) => {
      const update = (prev: Document[] = []) =>
        prev.map((task) =>
          task.id === doc.id || (doc.dbId && task.dbId === doc.dbId) ? { ...task, details: content, extraData } : task
        );
      queryClient.setQueryData<Document[]>(tasksQueryKey(sessionId), update);
      setServerTasks(update);
    },
    [queryClient, sessionId]
  );

  return {
    tasks,
    confirmedTasks,
    toggleCompletion,
    handleServerTasks,
    refreshTasks,
    updateTaskContent,
  };
};
